<template>
  <div class="clock-wizard">
    <!-- Job Selection Step -->
    <div v-if="currentStep === 'select-job'" class="wizard-step">
      <h5 class="mb-3">{{ wizardTitle }}</h5>
      
      <div class="mb-3" v-if="!preselectedTask">
        <label class="form-label">Client</label>
        <client-ac
          v-model="selectedClient"
          class="w-100"
          inputclass="form-control"
          placeholder="Select a client"
        />
      </div>
      
      <div class="mb-3" v-if="!preselectedTask">
        <job-ac
          v-model="selectedJob"
          :client="selectedClient"
          inputClass="form-control"
          :status="[0,1,11,12,13,21]"
          label="Job"
        />
      </div>

      <!-- Show task details if preselected -->
      <div v-if="preselectedTask" class="card bg-light mb-3">
        <div class="card-body">
          <p class="mb-1"><strong>Client:</strong> {{ taskData.Client?.Company }}</p>
          <p class="mb-1"><strong>Job:</strong> {{ taskData.Request?.TaskDetail || taskData.Request?.RequestDetailedDescription }}</p>
          <p class="mb-0" v-if="taskData.Task?.WorkAssignment">
            <strong>Task:</strong> {{ taskData.Task.WorkAssignment }}
          </p>
        </div>
      </div>

      <div class="d-flex gap-2 mt-4">
        <button class="btn btn-sm btn-outline-secondary flex-fill" @click="cancel">Cancel</button>
        <button class="btn btn-sm btn-outline-primary flex-fill" @click="proceedFromJobSelection">Next</button>
      </div>
    </div>

    <!-- Time Selection Step -->
    <div v-if="currentStep === 'select-time'" class="wizard-step">
      <h5 class="mb-3">{{ wizardTitle }}</h5>
      
      <div class="card bg-light mb-3">
        <div class="card-body">
          <p class="mb-1"><strong>Client:</strong> {{ taskData.Client?.Company }}</p>
          <p class="mb-1"><strong>Job:</strong> {{ taskData.Request?.TaskDetail || taskData.Request?.RequestDetailedDescription }}</p>
          <p class="mb-0" v-if="taskData.Task?.WorkAssignment">
            <strong>Task:</strong> {{ taskData.Task.WorkAssignment }}
          </p>
        </div>
      </div>

      <div v-if="operation === 'start'" class="d-grid gap-2">
        <button class="btn btn-sm btn-lg btn-outline-success" @click="startNow">
          <i class="fa fa-play me-2"></i>Start Now
        </button>
        <button class="btn btn-sm btn-outline-primary" @click="currentStep = 'pick-time'">
          <i class="fa fa-clock me-2"></i>Specify Start Time
        </button>
        <button class="btn btn-sm btn-outline-secondary" @click="currentStep = 'select-job'">
          <i class="fa fa-arrow-left me-2"></i>Back
        </button>
      </div>

      <div v-if="operation === 'stop'" class="d-grid gap-2">
        <button class="btn btn-sm btn-lg btn-outline-danger" @click="stopNow">
          <i class="fa fa-stop me-2"></i>Stop Now
        </button>
        <button class="btn btn-sm btn-outline-primary" @click="currentStep = 'pick-time'">
          <i class="fa fa-clock me-2"></i>Specify Stop Time
        </button>
        <button class="btn btn-sm btn-outline-secondary" @click="cancel">
          <i class="fa fa-times me-2"></i>Cancel
        </button>
      </div>
    </div>

    <!-- Pick Specific Time Step -->
    <div v-if="currentStep === 'pick-time'" class="wizard-step">
      <h5 class="mb-3">{{ wizardTitle }}</h5>
      
      <div class="card bg-light mb-3">
        <div class="card-body">
          <p class="mb-1"><strong>Client:</strong> {{ taskData.Client?.Company }}</p>
          <p class="mb-1"><strong>Job:</strong> {{ taskData.Request?.TaskDetail || taskData.Request?.RequestDetailedDescription }}</p>
          <p class="mb-0" v-if="taskData.Task?.WorkAssignment">
            <strong>Task:</strong> {{ taskData.Task.WorkAssignment }}
          </p>
        </div>
      </div>

      <div class="mb-3">
        <label class="form-label">Date</label>
        <input type="date" class="form-control" v-model="selectedDate" />
      </div>

      <div class="mb-3">
        <label class="form-label">Time</label>
        <input type="time" class="form-control" v-model="selectedTime" />
      </div>

      <div class="d-flex gap-2 mt-4">
        <button class="btn btn-sm btn-outline-secondary flex-fill" @click="currentStep = 'select-time'">Back</button>
        <button class="btn btn-sm btn-outline-primary flex-fill" @click="submitWithSpecificTime">
          {{ operation === 'start' ? 'Start Clock' : 'Stop Clock' }}
        </button>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="text-center py-4">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
      <p class="mt-2 text-muted">{{ loadingMessage }}</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { useRoute } from "vue-router";
import Api from "@/services/Api";
import ApiService from "@/services/ApiService";
import Helper from "@/services/Helper";
import Alert from "@/services/Alert";
import Cache from "@/services/Cache";
import ClockHelper from "@/components/Clock/ClockHelper";
import ClientAc from "@/components/Clients/ClientAc.vue";
import JobSelect from "@/components/Jobs/JobSelect.vue";
import JobAc from "@/components/Jobs/JobAc.vue";
import { TaskModel } from "@/models/TaskModel";

const props = defineProps({
  operation: {
    type: String,
    default: "start", // start, stop, pause, swap
    validator: (value: string) => ["start", "stop", "pause", "swap"].includes(value)
  },
  taskId: {
    type: [Number, String],
    default: null
  }
});

const emit = defineEmits(["cancel", "success"]);

const ch = new ClockHelper();
const route = useRoute();

// State
const currentStep = ref<string>("select-job");
const isLoading = ref(false);
const loadingMessage = ref("");

// Job selection
const selectedClient = ref(null);
const selectedJob = ref(null);
const preselectedTask = ref(false);

// Task data
const taskData = ref({
  Request: null,
  Client: null,
  Task: null,
  Clock: null
});

// Time selection
const selectedDate = ref(Helper.dateToInput(new Date(), Helper.DateFormat_Date));
const selectedTime = ref(Helper.dateToInput(new Date(), Helper.DateFormat_Time));

// Computed
const wizardTitle = computed(() => {
  switch (props.operation) {
    case "start":
      return "Start Clock";
    case "stop":
      return "Stop Clock";
    case "pause":
      return "Pause Clock";
    case "swap":
      return "Switch Customer";
    default:
      return "Clock Wizard";
  }
});

// Methods
const cancel = () => {
  emit("cancel");
};

const proceedFromJobSelection = async () => {
  if (!preselectedTask.value && (!selectedClient.value || !selectedJob.value)) {
    Alert.notify("Please select a client and a job");
    return;
  }

  isLoading.value = true;
  loadingMessage.value = "Loading job details...";

  try {
    if (!preselectedTask.value) {
      // Load the request
      const request = await ApiService.loadRequest(selectedJob.value);
      
      // Load the client separately using the CustomerNumber from the request
      let client = null;
      if (request.Customer) {
        client = await Api.loadClientByCustomerNumber(request.Customer);
      }
      
      taskData.value = {
        Request: request,
        Client: client,
        Task: null,
        Clock: null
      };
    }

    currentStep.value = "select-time";
  } catch (error) {
    console.error("Error loading job:", error);
    Alert.notify("Failed to load job details");
  } finally {
    isLoading.value = false;
  }
};

const startNow = async () => {
  await startClock(new Date());
};

const stopNow = async () => {
  await stopClock(new Date());
};

const submitWithSpecificTime = async () => {
  if (!selectedDate.value || !selectedTime.value) {
    Alert.notify("Please select a date and time");
    return;
  }

  const selectedDateTime = new Date(`${selectedDate.value} ${selectedTime.value}`);
  
  // Validate not in future
  if (selectedDateTime > new Date()) {
    Alert.notify("Please select a time in the past");
    return;
  }

  if (props.operation === "start") {
    await startClock(selectedDateTime);
  } else if (props.operation === "stop") {
    await stopClock(selectedDateTime);
  }
};

/**
 * Validates that we have all required data to start a clock
 */
const validateClockStartData = () => {
  if (!taskData.value.Request?.Id) {
    throw new Error("Cannot start clock: Request ID is missing");
  }
  if (!taskData.value.Client?.Id) {
    throw new Error("Cannot start clock: Client ID is missing");
  }
  
  const session = Cache.get("radar_user");
  if (!session?.UserId) {
    throw new Error("Cannot start clock: User session is invalid");
  }
  
  return session;
};

/**
 * Creates or updates a task for the clock
 * Returns the saved task with its ID
 */
const createOrUpdateTaskForClock = async (session: any): Promise<any> => {
  const inProgress = "Timeclock in progress on this task - DO NOT ALTER";
  
  let taskToSave: any;
  
  // If we have an existing task, update it
  if (taskData.value.Task?.Id) {
    console.log("Updating existing task:", taskData.value.Task.Id);
    taskToSave = new TaskModel(taskData.value.Task);
    taskToSave.Status = 11; // In Progress
    taskToSave.ActualWork = inProgress;
  } 
  // Otherwise create a new task
  else {
    console.log("Creating new task for request:", taskData.value.Request.Id);
    taskToSave = {
      CreateDate: new Date(),
      AssignedTo: session.UserId,
      RequestId: taskData.value.Request.Id,
      Status: 11, // In Progress
      ActualWork: inProgress,
    };
  }
  
  console.log("Saving task:", taskToSave);
  const savedTask = await ApiService.saveTime(taskToSave);
  console.log("Task saved - full response:", savedTask);
  console.log("Task saved - type:", typeof savedTask);
  console.log("Task saved - keys:", savedTask ? Object.keys(savedTask) : "null");
  console.log("Task saved - Id property:", savedTask?.Id);
  console.log("Task saved - id property (lowercase):", (savedTask as any)?.id);
  
  // Check both Id and id (case sensitivity issue?)
  const taskId = savedTask?.Id || (savedTask as any)?.id;
  
  if (!taskId) {
    console.error("Task save failed - no ID in response:", savedTask);
    throw new Error("Task save failed: No ID returned from server");
  }
  
  // Ensure the task object has the Id property set
  if (savedTask && !savedTask.Id && (savedTask as any).id) {
    savedTask.Id = (savedTask as any).id;
  }
  
  return savedTask;
};

/**
 * Creates and saves a clock linked to a task
 */
const createClockForTask = async (taskId: number, startTime: Date): Promise<any> => {
  const clockData = {
    TaskId: taskId,
    Start: startTime,
    Status: 1 // Running
  };
  
  console.log("Creating clock for task:", taskId, clockData);
  const savedClock = await ApiService.saveClock(clockData);
  console.log("Clock saved successfully:", savedClock);
  
  if (!savedClock?.Id) {
    throw new Error("Clock save failed: No ID returned from server");
  }
  
  return savedClock;
};

/**
 * Creates a note documenting when the clock started
 */
const createStartNote = async (taskId: number, session: any, startTime: Date): Promise<void> => {
  const noteData = {
    RequestId: taskData.value.Request.Id,
    TaskId: taskId,
    UserId: session.UserId,
    ClientId: taskData.value.Client.Id,
    DateTaken: startTime,
    Note: "Job Started: " + Helper.dateToInput(startTime, Helper.DateFormat_DateTime)
  };
  
  console.log("Creating start note:", noteData);
  await Api.saveNote(noteData);
  console.log("Start note saved successfully");
};

/**
 * Main function to start a clock
 */
const startClock = async (startTime: Date) => {
  isLoading.value = true;
  loadingMessage.value = "Starting clock...";

  try {
    // Step 1: Validate we have all required data
    const session = validateClockStartData();
    
    // Step 2: Create or update the task
    const savedTask = await createOrUpdateTaskForClock(session);
    
    // Step 3: Create the clock linked to the task
    const savedClock = await createClockForTask(savedTask.Id, startTime);
    
    // Step 4: Create a note documenting the start
    await createStartNote(savedTask.Id, session, startTime);
    
    // Success!
    console.log("Clock started successfully - Task ID:", savedTask.Id, "Clock ID:", savedClock.Id);
    Alert.notify("Clock started successfully", "success");
    emit("success", { task: savedTask, clock: savedClock });
    
  } catch (error: any) {
    console.error("Error starting clock:", error);
    Alert.notify("Failed to start clock: " + (error.message || "Unknown error"));
  } finally {
    isLoading.value = false;
  }
};

/**
 * Validates that we have all required data to stop a clock
 */
const validateClockStopData = () => {
  if (!taskData.value.Clock?.Id) {
    throw new Error("Cannot stop clock: Clock data is missing");
  }
  if (!taskData.value.Task?.Id) {
    throw new Error("Cannot stop clock: Task ID is missing");
  }
  if (!taskData.value.Request?.Id) {
    throw new Error("Cannot stop clock: Request ID is missing");
  }
  if (!taskData.value.Client?.Id) {
    throw new Error("Cannot stop clock: Client ID is missing");
  }
  
  const session = Cache.get("radar_user");
  if (!session?.UserId) {
    throw new Error("Cannot stop clock: User session is invalid");
  }
  
  return session;
};

/**
 * Calculates and updates the clock with stop time and paused time
 */
const updateClockWithStopTime = async (stopTime: Date): Promise<any> => {
  const clock = { ...taskData.value.Clock };
  
  console.log("Stopping clock:", clock.Id);
  
  // Set stop time
  clock.Stop = stopTime;
  clock.TimePaused = clock.TimePaused || 0;

  // Handle any active pause - add remaining pause time
  if (clock.Paused) {
    const pausedSeconds = Math.abs(ch.calcDiff(stopTime, new Date(clock.Paused)).seconds);
    clock.TimePaused += pausedSeconds;
    clock.Paused = null;
    console.log("Added final pause time:", pausedSeconds, "seconds");
  }

  // Save the clock with stop time
  const savedClock = await Api.saveClock(clock);
  console.log("Clock stopped and saved:", savedClock);
  
  return savedClock;
};

/**
 * Creates a note documenting when the clock stopped
 */
const createStopNote = async (taskId: number, session: any, stopTime: Date): Promise<void> => {
  const noteData = {
    RequestId: taskData.value.Request.Id,
    TaskId: taskId,
    UserId: session.UserId,
    ClientId: taskData.value.Client.Id,
    DateTaken: stopTime,
    Note: "Job Stopped: " + Helper.dateToInput(stopTime, Helper.DateFormat_DateTime)
  };
  
  console.log("Creating stop note:", noteData);
  await Api.saveNote(noteData);
  console.log("Stop note saved successfully");
};

/**
 * Compiles all notes into a formatted string for ActualWork field
 */
const compileNotesIntoActualWork = (notes: any[]): string => {
  if (!notes || notes.length === 0) {
    return "";
  }
  
  let noteStr = "";
  
  notes.forEach((note, i) => {
    if (note && note.Note) {
      let formattedNote = note.Note;
      
      // First note - append pipe character
      if (i === 0) {
        formattedNote = " - " + formattedNote + " | ";
      } 
      // Middle notes - add timestamp
      else if (i > 0 && i < notes.length - 1) {
        const timestamp = Helper.dateToInput(new Date(note.DateTaken), Helper.DateFormat_DateTime);
        formattedNote = " - " + timestamp + " - " + formattedNote;
      } 
      // Last note - preface with pipe character
      else if (i === notes.length - 1) {
        formattedNote = " | - " + formattedNote;
      }
      
      noteStr += formattedNote + "\n";
    }
  });
  
  return noteStr;
};

/**
 * Calculates total time and actual work time (minus pauses)
 */
const calculateWorkTime = (clock: any): { totalSeconds: number; workSeconds: number; workHours: string } => {
  const startDate = new Date(clock.Start);
  const stopDate = new Date(clock.Stop);
  
  const totalSeconds = Math.abs(ch.calcDiff(stopDate, startDate).seconds);
  const pausedSeconds = clock.TimePaused || 0;
  const workSeconds = totalSeconds - pausedSeconds;
  const workHours = (workSeconds / 3600).toFixed(2);
  
  console.log("Time calculation - Total:", totalSeconds, "Paused:", pausedSeconds, "Work:", workSeconds, "Hours:", workHours);
  
  return { totalSeconds, workSeconds, workHours };
};

/**
 * Updates the task with completion data
 */
const updateTaskWithCompletionData = async (taskId: number, clock: any, workHours: string, actualWork: string): Promise<any> => {
  console.log("Loading task for completion update:", taskId);
  const task = await Api.loadTime(taskId);
  
  // Update task fields
  task.WorkCompleteDate = Helper.dateToInput(new Date(clock.Stop), Helper.DateFormat_Date) + " 00:00:00";
  task.ActualTime = workHours;
  task.ActualWork = actualWork;
  
  console.log("Updating task with completion data:", {
    WorkCompleteDate: task.WorkCompleteDate,
    ActualTime: task.ActualTime,
    ActualWorkLength: actualWork.length
  });
  
  const savedTask = await Api.saveTime(task);
  console.log("Task updated successfully:", savedTask.Id);
  
  return savedTask;
};

/**
 * Main function to stop a clock
 */
const stopClock = async (stopTime: Date) => {
  isLoading.value = true;
  loadingMessage.value = "Stopping clock...";

  try {
    // Step 1: Validate we have all required data
    const session = validateClockStopData();
    
    // Step 2: Update and save the clock with stop time
    const stoppedClock = await updateClockWithStopTime(stopTime);
    
    // Step 3: Create a note documenting the stop
    await createStopNote(taskData.value.Task.Id, session, stopTime);
    
    // Step 4: Load the task with all its notes
    const taskWithNotes = await Api.loadTime(taskData.value.Task.Id);
    
    // Step 5: Compile all notes into ActualWork field
    const actualWork = compileNotesIntoActualWork(taskWithNotes.Notes);
    
    // Step 6: Calculate work time
    const { workHours } = calculateWorkTime(stoppedClock);
    
    // Step 7: Update task with completion data
    const completedTask = await updateTaskWithCompletionData(
      taskData.value.Task.Id,
      stoppedClock,
      workHours,
      actualWork
    );
    
    // Step 8: Save final clock state with total time
    stoppedClock.TotalTime = calculateWorkTime(stoppedClock).totalSeconds;
    await Api.saveClock(stoppedClock);
    
    // Success!
    console.log("Clock stopped successfully - Task ID:", completedTask.Id, "Clock ID:", stoppedClock.Id);
    Alert.notify("Clock stopped successfully", "success");
    emit("success", { task: completedTask, clock: stoppedClock });
    
  } catch (error: any) {
    console.error("Error stopping clock:", error);
    Alert.notify("Failed to stop clock: " + (error.message || "Unknown error"));
  } finally {
    isLoading.value = false;
  }
};

// Initialize
onMounted(async () => {
  // Check if we have a taskId from props or route
  const taskIdFromRoute = route.params.type;
  const taskId = props.taskId || taskIdFromRoute;

  if (taskId && !isNaN(Number(taskId))) {
    isLoading.value = true;
    loadingMessage.value = "Loading task...";

    try {
      const time = await ApiService.loadTime(String(taskId));

      // For start operation, check if clock already exists
      if (props.operation === "start" && time.Clock) {
        Alert.notify("This task already has a clock assigned to it.");
        emit("cancel");
        return;
      }

      // Set task data
      taskData.value = {
        Request: time.Request,
        Client: time.Contact,
        Task: time,
        Clock: time.Clock
      };

      preselectedTask.value = true;

      // For stop operation, skip to time selection
      if (props.operation === "stop") {
        currentStep.value = "select-time";
      }
    } catch (error) {
      console.error("Error loading task:", error);
      Alert.notify("Failed to load task");
      emit("cancel");
    } finally {
      isLoading.value = false;
    }
  }
});
</script>

<style scoped>
.clock-wizard {
  min-height: 300px;
}

.wizard-step {
  animation: fadeIn 0.3s ease-in;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.gap-2 {
  gap: 0.5rem;
}
</style>
